/**
 * Trust Score Badge — top-nav resident.
 *
 * Rationale (per operator directive 2026-07-31): every dispatcher, supervisor,
 * and administrator sees the Platform Trust Score all day long. That's how the
 * number becomes a shared goal. This is not another dashboard; it's the
 * flagship KPI made ambient.
 *
 * Reads from GET /api/admin/trust-ops/overview. Click → navigates to
 * /admin/trust-ops for the full breakdown.
 */

import React, { useEffect, useState, useCallback } from "react";
import { apiClient } from "../lib/api";

const REFRESH_MS = 60_000;

const gradeColor = (grade) => {
  if (grade === "A") return "#047857"; // green
  if (grade === "B") return "#0EA5E9"; // sky
  if (grade === "C") return "#B45309"; // amber
  return "#B91C1C";                     // red for D / F
};

export default function TrustScoreBadge({ onClick }) {
  const [state, setState] = useState({ loading: true, score: null, grade: null, error: false });

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/admin/trust-ops/overview");
      setState({ loading: false, score: data?.trust_score ?? null, grade: data?.grade || "F", error: false });
    } catch {
      // Preserve last-known-good score instead of showing "n/a" on a transient
      // network hiccup. Only flip to error if we have NEVER loaded a score.
      setState(prev => (prev.score != null
        ? { ...prev, loading: false, error: false }
        : { loading: false, score: null, grade: null, error: true }));
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const { loading, score, grade, error } = state;
  const col = grade ? gradeColor(grade) : "#94A3B8";

  return (
    <button
      type="button"
      data-testid="admin-trust-score-badge"
      onClick={onClick}
      title="Platform Trust Score — click for full breakdown"
      className="inline-flex items-center gap-2 rounded-md border border-[#E2E0D8] bg-white hover:bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate_navy transition-colors"
    >
      <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 hidden md:inline">Trust</span>
      {loading ? (
        <span className="text-slate-400 text-xs">—</span>
      ) : error ? (
        <span className="text-slate-400 text-xs" data-testid="admin-trust-score-badge-error">n/a</span>
      ) : (
        <>
          <span className="font-heading text-sm font-extrabold tabular-nums" data-testid="admin-trust-score-value" style={{ color: col }}>
            {typeof score === "number" ? score.toFixed(1) : "—"}
          </span>
          {grade && (
            <span
              data-testid="admin-trust-score-grade"
              style={{ background: col, color: "#fff" }}
              className="text-[10px] font-extrabold rounded px-1.5 py-0.5 leading-none"
            >
              {grade}
            </span>
          )}
        </>
      )}
    </button>
  );
}
